// src/lib/seo/routes.ts
//
// Typed accessors over src/lib/seo/public-routes.json, which scripts/route-map.mjs
// also reads. Keeping the data in JSON means the runtime <SeoHead> and the
// build-time sitemap/prerender cannot drift apart.

import data from "./public-routes.json";
import { publishedComparisons } from "../../data/comparisons";

export type Locale = "en" | "fr" | "es";

export interface Alternate {
  hreflang: string;
  href: string;
}

export interface SeoEntry {
  canonical: string;
  alternates: Alternate[];
}

export const ORIGIN: string = data.origin;
const X_DEFAULT: Locale = data.xDefaultLocale as Locale;

const LOCALES: Locale[] = ["en", "fr", "es"];

/**
 * path -> { canonical, alternates }
 *
 * Built once at module load. x-default points at the fullest locale so a visitor
 * with no matching language gets real content rather than a partial translation.
 */
const ALTERNATES: Map<string, SeoEntry> = (() => {
  const map = new Map<string, SeoEntry>();

  // Published comparisons are derived, not listed in the JSON — see the note in
  // public-routes.json. scripts/route-map.mjs derives the same set for the
  // sitemap and prerender list.
  const comparePages = publishedComparisons().map((c) => ({
    paths: { en: `/compare/${c.slug}` },
  }));

  for (const page of [...data.pages, ...comparePages]) {
    const paths = page.paths as Partial<Record<Locale, string>>;
    const present = LOCALES.filter((l) => paths[l]);

    const alternates: Alternate[] = present.map((l) => ({
      hreflang: l,
      href: ORIGIN + paths[l]!,
    }));

    const fallback = paths[X_DEFAULT] ?? paths[present[0]]!;
    alternates.push({ hreflang: "x-default", href: ORIGIN + fallback });

    for (const l of present) {
      map.set(paths[l]!, { canonical: ORIGIN + paths[l]!, alternates });
    }
  }

  return map;
})();

/** Trailing slashes are stripped so /retailers/ and /retailers resolve alike. */
function normalize(pathname: string): string {
  if (pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

/**
 * SEO head data for a pathname, or null when the path is not part of the public
 * marketing surface (auth routes, the portal, the noindexed booking forms).
 *
 * Product AR pages are single-locale and data-driven, so they get a canonical
 * derived from the path with no hreflang cluster.
 */
export function seoForPath(pathname: string): SeoEntry | null {
  const p = normalize(pathname);

  const entry = ALTERNATES.get(p);
  if (entry) return entry;

  if (p.startsWith("/demo/")) {
    return { canonical: ORIGIN + p, alternates: [] };
  }

  return null;
}
